/**
 * ADK Platform Frontend
 * Main application component with all providers
 */

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { queryClient } from "@services/queryClient";
import { AuthProvider } from "@contexts/AuthContext";
import { TenantProvider } from "@contexts/TenantContext";
import { ThemeProvider } from "@contexts/ThemeContext";
import { RootLayout, ProtectedRoute } from "@components/layout";

// Page imports
import { DashboardPage } from "@pages/dashboard";
import { WorkshopsPage, WorkshopDetailPage } from "@pages/workshops";
import { ExercisePage } from "@pages/exercises";
import { GuidePage, GuidesListPage } from "@pages/guides";
import { LibraryListPage, LibraryResourcePage } from "@pages/library";
import { ToolsListPage, ToolDetailPage } from "@pages/tools";
import { NewsListPage, NewsDetailPage } from "@pages/news";
import { TemplateLibraryPage, TemplateDetailPage } from "@pages/templates";
import { ExamplePage } from "@pages/examples";
import {
  PlatformAdminPage,
  OrgAdminPage,
  AdminUsersPage,
  AdminTenantsPage,
  AdminLibraryPage,
  AdminGuidesPage,
  WorkshopPreviewPage,
} from "@pages/admin";
import { SettingsPage } from "@pages/profile";
import { SetupWizard } from "@pages/getting-started";
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  WelcomePage,
  VerifyEmailChangePage,
} from "@pages/auth";
import { PrivacyPolicyPage, TermsOfServicePage } from "@pages/legal";
import { EnrollPage } from "@pages/enroll/EnrollPage";

// Placeholder pages - will be replaced with actual page components
function AgentsPage() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
        Agents
      </h1>
      <p className="text-gray-600 dark:text-gray-300">
        Agent management coming soon...
      </p>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-300 dark:text-gray-600">
          404
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-400 mt-4">
          Page not found
        </p>
      </div>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <TenantProvider>
            <BrowserRouter
              future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
            >
              <Routes>
                {/* Public auth routes (no layout) */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route
                  path="/forgot-password"
                  element={<ForgotPasswordPage />}
                />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/welcome" element={<WelcomePage />} />
                <Route
                  path="/verify-email-change"
                  element={<VerifyEmailChangePage />}
                />

                {/* Cohort Self-Registration (public) */}
                <Route
                  path="/enroll/:inviteCode"
                  element={<EnrollPage />}
                />

                {/* Legal Pages - Public */}
                <Route
                  path="/privacy"
                  element={
                    <RootLayout>
                      <PrivacyPolicyPage />
                    </RootLayout>
                  }
                />
                <Route
                  path="/terms"
                  element={
                    <RootLayout>
                      <TermsOfServicePage />
                    </RootLayout>
                  }
                />

                {/* Dashboard */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <DashboardPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Workshops */}
                <Route
                  path="/workshops"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <WorkshopsPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/workshops/:id"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <WorkshopDetailPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Exercises */}
                <Route
                  path="/exercises/:id"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <ExercisePage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Guides */}
                <Route
                  path="/guides"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <GuidesListPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/guides/:slug"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <GuidePage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Library */}
                <Route
                  path="/library"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <LibraryListPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/library/:id"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <LibraryResourcePage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Tools Marketplace */}
                <Route
                  path="/tools"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <ToolsListPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tools/:id"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <ToolDetailPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Templates */}
                <Route
                  path="/templates"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <TemplateLibraryPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/templates/:id"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <TemplateDetailPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />

                {/* News */}
                <Route
                  path="/news"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <NewsListPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/news/:id"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <NewsDetailPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Getting Started / Setup Wizard */}
                <Route
                  path="/getting-started"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <SetupWizard />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Examples */}
                <Route
                  path="/examples/:id"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <ExamplePage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Agents */}
                <Route
                  path="/agents"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <AgentsPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Visual Builder is accessed via external ADK CLI at http://localhost:8000/dev-ui */}

                {/* Profile Routes */}
                <Route
                  path="/profile/settings"
                  element={
                    <ProtectedRoute>
                      <RootLayout>
                        <SettingsPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Platform Admin Console - Super Admin only */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <RootLayout>
                        <PlatformAdminPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Organization Admin Console - Tenant Admin and above */}
                <Route
                  path="/org-admin"
                  element={
                    <ProtectedRoute requiredRole="tenant_admin">
                      <RootLayout>
                        <OrgAdminPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />
                {/* Legacy admin routes - redirect to unified admin */}
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute requiredRole="tenant_admin">
                      <RootLayout>
                        <AdminUsersPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/tenants"
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <RootLayout>
                        <AdminTenantsPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/library"
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <RootLayout>
                        <AdminLibraryPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/guides"
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <RootLayout>
                        <AdminGuidesPage />
                      </RootLayout>
                    </ProtectedRoute>
                  }
                />
                {/* Workshop Preview - Admin/Instructor only */}
                <Route
                  path="/admin/workshops/:workshopId/preview"
                  element={
                    <ProtectedRoute requiredRole="instructor">
                      <WorkshopPreviewPage />
                    </ProtectedRoute>
                  }
                />

                {/* Catch-all */}
                <Route
                  path="/404"
                  element={
                    <RootLayout>
                      <NotFoundPage />
                    </RootLayout>
                  }
                />
                <Route path="*" element={<Navigate to="/404" replace />} />
              </Routes>
            </BrowserRouter>
          </TenantProvider>
        </AuthProvider>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
